import type { Office } from "./types"
import { getAreaGuidesForLocation, type AreaGuide } from "@/data/areas/registry"

/* ------------------------------------------------------------------ */
/* Deterministic variation                                             */
/* ------------------------------------------------------------------ */

/** Simple hash of a string to a positive integer for deterministic variation. */
function hashSlug(slug: string): number {
  let hash = 0
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

/** Pick one item from an array deterministically based on slug. */
function pick<T>(slug: string, items: T[]): T {
  return items[hashSlug(slug) % items.length]
}

/* ------------------------------------------------------------------ */
/* Services prose (Section B)                                          */
/* ------------------------------------------------------------------ */

const SERVICE_OPENERS = [
  (name: string, branch: string) =>
    `${name} ${branch} offers a broad range of property services`,
  (name: string, branch: string) =>
    `Based in ${branch}, ${name} provides comprehensive estate agency services`,
  (name: string, branch: string) =>
    `${name}'s ${branch} office delivers a full suite of property services`,
  (name: string, branch: string) =>
    `The ${branch} team at ${name} covers a wide range of property services`,
]

const AREA_CONNECTORS = [
  "Their reach extends across",
  "The team covers",
  "They operate across",
  "Their local knowledge spans",
]

const PRICE_CLOSERS = [
  (avg: string) =>
    `with an average property price of around ${avg}.`,
  (avg: string) =>
    `where the average asking price sits at approximately ${avg}.`,
  (avg: string) =>
    `with properties typically marketed at around ${avg}.`,
]

export function generateServicesProse(office: Office, cityName: string): string {
  const opener = pick(office.slug, SERVICE_OPENERS)(office.name, office.branch)
  const services = formatList(office.specialisms.map((s) => s.toLowerCase()))
  const connector = pick(office.slug + "-area", AREA_CONNECTORS)
  const areas = formatList(office.areas.slice(0, 6))
  const closer = pick(office.slug + "-price", PRICE_CLOSERS)(office.avgPrice)

  return `${opener}, including ${services}. ${connector} ${areas} and the wider ${cityName} area, ${closer}`
}

/* ------------------------------------------------------------------ */
/* Local market content (Section C)                                    */
/* ------------------------------------------------------------------ */

export interface LocalMarketContent {
  heading: string
  paragraphs: string[]
  areaGuideHref: string | null
  areaGuideName: string | null
}

/**
 * Find the best matching area guide for an office.
 * Tries the branch name first, then falls back to the first covered area.
 */
function findMatchingAreaGuide(
  office: Office,
  location: string
): AreaGuide | undefined {
  const guides = getAreaGuidesForLocation(location)
  if (guides.length === 0) return undefined

  // Try to match the branch name (slugified)
  const branchSlug = office.branch.toLowerCase().replace(/[\s-]+/g, "-")
  const match = guides.find((g) => g.slug === branchSlug)
  if (match) return match

  // Try the first few covered areas
  for (const area of office.areas.slice(0, 3)) {
    const areaSlug = area.toLowerCase().replace(/[\s-]+/g, "-")
    const found = guides.find((g) => g.slug === areaSlug)
    if (found) return found
  }

  return undefined
}

const MARKET_INTROS = [
  (name: string, area: string) =>
    `As a ${area}-based agent, ${name} is well placed to advise on the local property market.`,
  (name: string, area: string) =>
    `${name} operates at the heart of ${area}'s property market.`,
  (name: string, area: string) =>
    `With their ${area} base, ${name} has deep insight into the local market.`,
]

export function getLocalMarketContent(
  office: Office,
  location: string,
  cityName: string
): LocalMarketContent {
  const guide = findMatchingAreaGuide(office, location)

  if (guide) {
    const intro = pick(office.slug + "-market", MARKET_INTROS)(
      office.name,
      guide.name
    )

    // Take first paragraph of the area guide description
    const descParagraphs = guide.description.split("\n\n")
    const excerpt = descParagraphs[0]

    const propertyTypes =
      guide.propertyTypes.length > 0
        ? `Property in the area includes ${formatList(guide.propertyTypes.map((t) => t.toLowerCase()))}, with an average price of ${guide.avgPrice}.`
        : ""

    return {
      heading: `The ${guide.name} property market`,
      paragraphs: [intro, excerpt, propertyTypes].filter(Boolean),
      areaGuideHref: `/areas/${location}/${guide.slug}`,
      areaGuideName: guide.name,
    }
  }

  // Fallback: city-level content
  return {
    heading: `The ${cityName} property market`,
    paragraphs: [
      `${office.name} operates in ${cityName}, one of the UK's most dynamic property markets. The city offers a diverse range of homes, from period townhouses and conversions to modern apartments and family houses across its many distinctive neighbourhoods.`,
    ],
    areaGuideHref: `/areas/${location}`,
    areaGuideName: `${cityName} area guides`,
  }
}

/* ------------------------------------------------------------------ */
/* Utility                                                             */
/* ------------------------------------------------------------------ */

/** Format an array as a natural English list: "a, b, and c". */
function formatList(items: string[]): string {
  if (items.length === 0) return ""
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}
